import { AImagineActionType } from './enums';
import { type AImagineAction, type AImagineState } from './interfaces';

export const initialState: AImagineState = {
	// Generation result
	generatedImageUrl: null,
	generatedDocumentId: null,
	generatedReferenceId: null,
	generatedDocumentUrlId: null,

	// Input image (set after successful upload + activate)
	uploadedImageUrl: null,
	uploadedDocumentId: null,

	// Loading / error
	isUploading: false,
	isGenerating: false,
	generateError: null,

	// Form / UI state
	selectedTool: null,
	toolOptions: {
		roomType: 'room',
		designStyle: 'modern',
		flooringType: 'hardwood',
		wallColor: 'white',
		colorTheme: 'neutral',
		ceilingType: 'tray ceiling'
	},
	prompt: '',
	areaName: '',
	documentId: null
};

export default (state = initialState, { type, payload }: AImagineAction): AImagineState => {
	switch (type) {
		// -----------------------------------------------------------------------
		// Upload
		// -----------------------------------------------------------------------

		// Set isUploading: true as soon as the trigger action is dispatched.
		// The saga picks this up via takeLatest — the reducer sees it first.
		case AImagineActionType.UPLOAD_INPUT_IMAGE:
			return {
				...state,
				isUploading: true,
				// Clear previous upload so the canvas doesn't show stale server URL
				uploadedImageUrl: null,
				uploadedDocumentId: null
			};

		// Saga dispatches this after uploadFile + activateDocument both succeed.
		// imageUrl is the viewable CDN/server URL (may be null if activate response
		// doesn't carry a URL — component falls back to its base64 preview in that case).
		case AImagineActionType.SET_UPLOADED_IMAGE:
			return {
				...state,
				isUploading: false,
				uploadedImageUrl: payload.imageUrl ?? null,
				uploadedDocumentId: payload.documentId ?? null
			};

		// -----------------------------------------------------------------------
		// Generation
		// -----------------------------------------------------------------------

		case AImagineActionType.SET_GENERATE_LOADING:
			return {
				...state,
				isGenerating: payload
			};

		case AImagineActionType.STORE_GENERATED_IMAGE:
			return {
				...state,
				isGenerating: false,
				generateError: null,
				generatedImageUrl: payload.imageUrl ?? null,
				generatedDocumentId: payload.documentId ?? null,
				generatedDocumentUrlId: payload.documentUrlId ?? null,
				generatedReferenceId: payload.referenceId ?? null
			};

		case AImagineActionType.SET_GENERATE_ERROR:
			return {
				...state,
				isUploading: false,
				isGenerating: false,
				generateError: payload
			};

		case AImagineActionType.CLEAR_GENERATE_ERROR:
			return {
				...state,
				generateError: null
			};

		// -----------------------------------------------------------------------
		// Form / UI
		// -----------------------------------------------------------------------

		case AImagineActionType.SET_TOOL:
			return {
				...state,
				selectedTool: payload
			};

		case AImagineActionType.SET_TOOL_OPTIONS:
			return {
				...state,
				toolOptions: {
					...state.toolOptions,
					...payload
				}
			};

		case AImagineActionType.SET_PROMPT:
			return {
				...state,
				prompt: payload
			};

		case AImagineActionType.SET_AREA_NAME:
			return {
				...state,
				areaName: payload
			};

		case AImagineActionType.RESET_STATE:
			return { ...initialState };

		default:
			return state;
	}
};
